import { WorkflowNode, WorkflowNodeType, nodeTypeLabelFor } from '../models';
import { NodePosition } from './workflow.state';

/*
 * Canvas layout for workflow graphs. Positions are UI-only and the API does
 * not store them, so every workflow loaded from the server needs a layout
 * computed from its structure.
 *
 * The geometry mirrors the canvas card styles (workflow-canvas.component.scss):
 * cards are content-sized between 180px and 320px wide and at least 64px tall,
 * growing taller when long names wrap.
 *
 * The graph is laid out in layers either way; orientation only decides which
 * screen axis the layers advance along. Edges need no say in it — the canvas
 * anchors each one to whichever side of a card faces its target.
 */

/** Which way the graph flows: layer to layer across the screen, or down it. */
export type WorkflowOrientation = 'horizontal' | 'vertical';

export const DEFAULT_WORKFLOW_ORIENTATION: WorkflowOrientation = 'vertical';

/**
 * Stamped on saved drafts. Drafts without it (or with an older value) hold
 * positions from a previous layout scheme and are laid out afresh on restore.
 */
export const WORKFLOW_LAYOUT_VERSION = 2;

const ORIGIN_X = 80;
const ORIGIN_Y = 80;
/** Space between a layer's largest card and the next layer. */
const LAYER_GAP = 80;
/** Tallest card we expect (a name wrapping onto three lines). */
const MAX_CARD_HEIGHT = 100;
const ROW_GAP = 40;
const ROW_PITCH = MAX_CARD_HEIGHT + ROW_GAP;
const MIN_CARD_WIDTH = 180;
const MAX_CARD_WIDTH = 320;
const MIN_CARD_HEIGHT = 64;
/** Grid used to find a free spot for nodes added from the palette. */
const FREE_SPOT_PITCH_X = MAX_CARD_WIDTH + 60;
const FREE_SPOT_CLEARANCE = 20;
/** How many lanes the search fills across the flow before advancing along it. */
const FREE_SPOT_LANES = 4;

export interface NodeSize {
  width: number;
  height: number;
}

/** Size of the visible canvas. Lets a graph be centred in it rather than left in a corner. */
export interface CanvasViewport {
  width: number;
  height: number;
}

/**
 * Where auto-layout placed a node: how far along the flow it sits (`layer`) and
 * which lane across it (`row`, fractional when centred between branches). Both
 * are orientation-free — `arrangeLaidOutNodes` decides which screen axis each
 * one maps to. A `pinned` node has been dragged by hand — it keeps its position
 * but still counts towards its layer's extent, so other layers don't shift when
 * it moves.
 */
export interface LayoutSlot {
  layer: number;
  row: number;
  pinned?: boolean;
}

export interface WorkflowLayout {
  positions: Record<string, NodePosition>;
  slots: Record<string, LayoutSlot>;
}

/**
 * Lay the graph out in layers along the flow: entry nodes (triggers, and any
 * node nothing points at) in the first layer, each node one layer past the
 * deepest node leading into it. Branches stack into lanes, true branch first.
 * Loops are tolerated — the edge that closes a loop is ignored for layering.
 * Initial positions use estimated card sizes; `arrangeLaidOutNodes` re-runs
 * with measured sizes once the canvas renders.
 */
export function layoutWorkflow(
  nodes: WorkflowNode[],
  orientation: WorkflowOrientation = DEFAULT_WORKFLOW_ORIENTATION,
  viewport: CanvasViewport | null = null
): WorkflowLayout {
  if (nodes.length === 0) {
    return { positions: {}, slots: {} };
  }

  const byId = new Map(nodes.map(node => [node.id, node]));
  const successorsOf = (node: WorkflowNode): string[] =>
    [...new Set([node.successorTrue, node.successorFalse])].filter(
      (id): id is string => !!id && id !== node.id && byId.has(id)
    );

  const hasIncoming = new Set(nodes.flatMap(successorsOf));
  const roots = [
    ...nodes.filter(node => node.type === WorkflowNodeType.Trigger),
    ...nodes.filter(node => node.type !== WorkflowNodeType.Trigger && !hasIncoming.has(node.id))
  ];

  // Depth-first walk: records discovery order and the acyclic predecessors of
  // each node. An edge back to a node still on the walk closes a loop, so it
  // is left out of the layering.
  const visitState = new Map<string, 'active' | 'done'>();
  const discoveryOrder: string[] = [];
  const postOrder: string[] = [];
  const predecessors = new Map<string, string[]>(nodes.map(node => [node.id, []]));

  const visit = (id: string): void => {
    visitState.set(id, 'active');
    discoveryOrder.push(id);
    for (const next of successorsOf(byId.get(id)!)) {
      const state = visitState.get(next);
      if (state === 'active') {
        continue;
      }
      predecessors.get(next)!.push(id);
      if (state === undefined) {
        visit(next);
      }
    }
    visitState.set(id, 'done');
    postOrder.push(id);
  };

  for (const root of roots) {
    if (!visitState.has(root.id)) visit(root.id);
  }
  // Anything left is only reachable from inside a loop with no entry point.
  for (const node of nodes) {
    if (!visitState.has(node.id)) visit(node.id);
  }

  // Longest-path layering over the acyclic edges (reverse post-order is a
  // topological order), so a node sits right of every node leading into it.
  const layerOf = new Map<string, number>();
  for (const id of [...postOrder].reverse()) {
    const incoming = predecessors.get(id)!;
    layerOf.set(id, incoming.length ? Math.max(...incoming.map(p => layerOf.get(p)! + 1)) : 0);
  }

  const layers: string[][] = [];
  for (const id of discoveryOrder) {
    (layers[layerOf.get(id)!] ??= []).push(id);
  }

  // Order each layer by the average row of the nodes leading into it to keep
  // arrows from crossing; ties keep walk order (true branch first).
  const discoveryIndex = new Map(discoveryOrder.map((id, index) => [id, index]));
  const rowOffset = new Map<string, number>();
  const averageIncomingRow = (id: string): number => {
    const incoming = predecessors.get(id)!;
    return incoming.reduce((sum, p) => sum + rowOffset.get(p)!, 0) / incoming.length;
  };

  layers.forEach((layer, depth) => {
    if (depth > 0) {
      layer.sort(
        (a, b) =>
          averageIncomingRow(a) - averageIncomingRow(b) ||
          discoveryIndex.get(a)! - discoveryIndex.get(b)!
      );
    }
    // Rows are centred on 0 so shorter columns sit in the middle.
    layer.forEach((id, index) => rowOffset.set(id, index - (layer.length - 1) / 2));
  });

  const widestHalfSpan = Math.max(...layers.map(layer => (layer.length - 1) / 2));
  const slots: Record<string, LayoutSlot> = {};
  for (const node of nodes) {
    slots[node.id] = { layer: layerOf.get(node.id)!, row: rowOffset.get(node.id)! + widestHalfSpan };
  }

  return {
    slots,
    positions: arrangeLaidOutNodes(
      slots,
      id => estimateCardSize(byId.get(id)!),
      orientation,
      viewport
    )
  };
}

/**
 * Turn layout slots into positions for the given card sizes. Each layer is as
 * big as its largest card, with a fixed gap before the next, and every card is
 * centred on its layer and lane — so a straight chain of cards has even gaps
 * and perfectly straight arrows. Given the canvas size, the graph is also
 * centred across the flow: down the middle of the canvas when it runs
 * vertically, along the middle when it runs across. Pinned (hand-placed) nodes
 * shape the layers but get no position here.
 */
export function arrangeLaidOutNodes(
  slots: Record<string, LayoutSlot>,
  sizeOf: (id: string) => NodeSize,
  orientation: WorkflowOrientation = DEFAULT_WORKFLOW_ORIENTATION,
  viewport: CanvasViewport | null = null
): Record<string, NodePosition> {
  const entries = Object.entries(slots);
  if (entries.length === 0) return {};
  const vertical = orientation === 'vertical';
  const alongOf = (id: string): number => (vertical ? sizeOf(id).height : sizeOf(id).width);
  const acrossOf = (id: string): number => (vertical ? sizeOf(id).width : sizeOf(id).height);

  const layerExtents: number[] = [];
  for (const [id, slot] of entries) {
    layerExtents[slot.layer] = Math.max(layerExtents[slot.layer] ?? 0, alongOf(id));
  }

  const layerStart: number[] = [];
  let along = vertical ? ORIGIN_Y : ORIGIN_X;
  for (let layer = 0; layer < layerExtents.length; layer++) {
    layerStart[layer] = along;
    along += (layerExtents[layer] ?? 0) + LAYER_GAP;
  }

  // A lane can be fractional (a card centred between its branches), so there is
  // no single card to measure it against and the cross axis uses one pitch
  // throughout. Heights barely vary, so laying out across them keeps the
  // constant; widths run from 180 to 320, so laying out across those measures
  // the graph's widest card rather than assuming every card is the widest.
  const laneExtent = vertical
    ? Math.max(MIN_CARD_WIDTH, ...entries.map(([id]) => acrossOf(id)))
    : MAX_CARD_HEIGHT;
  const lanePitch = laneExtent + ROW_GAP;

  // Centre the lanes across the canvas, so a single chain runs down the middle
  // rather than hugging one edge. A graph wider than the canvas falls back to
  // the origin padding and scrolls, which beats pushing its first lane
  // off-screen.
  const rows = entries.map(([, slot]) => slot.row);
  const firstRow = Math.min(...rows);
  const graphExtent = (Math.max(...rows) - firstRow) * lanePitch + laneExtent;
  const canvasExtent = vertical ? viewport?.width : viewport?.height;
  const basePadding = vertical ? ORIGIN_X : ORIGIN_Y;
  const laneOrigin =
    Math.max(basePadding, canvasExtent ? (canvasExtent - graphExtent) / 2 : basePadding) -
    firstRow * lanePitch;

  const positions: Record<string, NodePosition> = {};
  for (const [id, slot] of entries) {
    if (slot.pinned) continue;
    const size = sizeOf(id);
    const alongCentre = layerStart[slot.layer] + layerExtents[slot.layer] / 2;
    const laneCentre = laneOrigin + laneExtent / 2 + slot.row * lanePitch;
    positions[id] = vertical
      ? {
          x: Math.round(laneCentre - size.width / 2),
          y: Math.round(alongCentre - size.height / 2)
        }
      : {
          x: Math.round(alongCentre - size.width / 2),
          y: Math.round(laneCentre - size.height / 2)
        };
  }
  return positions;
}

/**
 * Position for a node added from the palette: the first free spot, searched in
 * the direction the graph flows, so a new card lands past the last one rather
 * than off to the side of it.
 */
export function nextFreeNodePosition(
  nodes: WorkflowNode[],
  positions: Record<string, NodePosition>,
  orientation: WorkflowOrientation = DEFAULT_WORKFLOW_ORIENTATION
): NodePosition {
  const occupied = nodes
    .filter(node => positions[node.id])
    .map(node => ({ ...positions[node.id], width: estimateCardSize(node).width }));
  const vertical = orientation === 'vertical';

  for (let along = 0; ; along++) {
    for (let lane = 0; lane < FREE_SPOT_LANES; lane++) {
      const col = vertical ? along : lane;
      const row = vertical ? lane : along;
      const candidate = { x: ORIGIN_X + col * FREE_SPOT_PITCH_X, y: ORIGIN_Y + row * ROW_PITCH };
      const overlaps = occupied.some(
        card =>
          candidate.x < card.x + card.width + FREE_SPOT_CLEARANCE &&
          card.x < candidate.x + MAX_CARD_WIDTH + FREE_SPOT_CLEARANCE &&
          candidate.y < card.y + MAX_CARD_HEIGHT + FREE_SPOT_CLEARANCE &&
          card.y < candidate.y + MAX_CARD_HEIGHT + FREE_SPOT_CLEARANCE
      );
      if (!overlaps) {
        return candidate;
      }
    }
  }
}

/**
 * Approximate rendered card size from its labels: name at 0.85rem semibold,
 * type label at 0.65rem uppercase, plus padding and borders. Stands in until
 * the canvas reports measured sizes.
 */
export function estimateCardSize(node: WorkflowNode): NodeSize {
  const name = node.name || 'Untitled';
  const typeLabel = nodeTypeLabelFor(node);
  const contentWidth = Math.max(name.length * 7.6, typeLabel.length * 7);
  const width = Math.min(MAX_CARD_WIDTH, Math.max(MIN_CARD_WIDTH, contentWidth + 32));
  const nameLines = Math.max(1, Math.ceil((name.length * 7.6) / (MAX_CARD_WIDTH - 32)));
  return { width, height: Math.max(MIN_CARD_HEIGHT, 38 + nameLines * 17) };
}
